import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../../context/AuthContext';
import ThemeContext from '../../context/ThemeContext';
import axios from 'axios';
import {
    Box,
    Button,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Icon,
    Card,
    CardBody,
    Flex,
    Spinner,
    Alert,
    AlertIcon,
    AlertTitle,
    AlertDescription,
    Badge,
    useBreakpointValue,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    useToast,
    Divider,
    Progress,
    Input,
    InputGroup,
    InputLeftElement,
    IconButton,
    Collapse,
} from '@chakra-ui/react';
import {
    FaPhone,
    FaUsers,
    FaSync,
    FaMapMarkerAlt,
    FaUser,
    FaLightbulb,
    FaChartLine,
    FaShieldAlt,
    FaListUl,
    FaSearch,
} from 'react-icons/fa';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

function DashboardRMwhoToCallPaid() {
    const { token } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const [callList, setCallList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCustomer, setExpandedCustomer] = useState(null);
    const toast = useToast();

    const statSize = useBreakpointValue({ base: 'sm', md: 'md' });

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Card background - improved for dark theme
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';

    // Text colors with proper contrast - following Dashboard.js pattern
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';

    // Border colors
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const lightBorderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.2)' : 'gray.100';

    // Info card backgrounds - improved transparency for dark theme
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.15)' : accentColor + '10';
    const actionBg = isDarkTheme ? 'rgba(99, 179, 237, 0.15)' : 'blue.50';

    // Button hover
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;

    // Icon colors
    const mutedIconColor = isDarkTheme ? mutedTextColor : 'gray.400';

    const fetchWhoToCall = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/content/rm-who-to-call`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setCallList(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Error fetching who to call:', err);
            const msg = err.response?.data?.message || 'Failed to fetch call recommendations';
            setError(msg);
            toast({
                title: 'Error',
                description: msg,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) {
            fetchWhoToCall();
        }
        // eslint-disable-next-line
    }, [token]);

    const getUrgencyColor = (urgencyLevel) => {
        const level = urgencyLevel?.toLowerCase();
        if (level === 'urgent' || level === 'high') return 'red';
        if (level === 'medium' || level === 'moderate') return 'orange';
        return 'green';
    };

    const getPriorityColor = (score) => {
        if (score >= 8) return 'red';
        if (score >= 6) return 'orange';
        if (score >= 4) return 'yellow';
        return 'green';
    };

    const getRiskColor = (riskProfile) => {
        if (riskProfile <= 3) return 'green';
        if (riskProfile <= 6) return 'yellow';
        if (riskProfile <= 8) return 'orange';
        return 'red';
    };

    const getRiskLabel = (riskProfile) => {
        if (riskProfile <= 3) return 'Low';
        if (riskProfile <= 6) return 'Medium';
        if (riskProfile <= 8) return 'High';
        return 'Very High';
    };

    // Toggle customer details
    const toggleCustomer = (customerId) => {
        if (expandedCustomer === customerId) {
            setExpandedCustomer(null);
        } else {
            setExpandedCustomer(customerId);
        }
    };

    // Handle search
    const handleSearch = () => {
        // Search is handled by filteredCallList already
    };

    // ---- Stats ----
    const avgPriority =
        callList.length > 0
            ? (callList.reduce((sum, c) => sum + c.priority_score, 0) / callList.length).toFixed(2)
            : 0;
    const highUrgencyCount = callList.filter(
        (c) => c.urgency_level?.toLowerCase() === 'high' || c.urgency_level?.toLowerCase() === 'urgent'
    ).length;
    const filteredCallList = callList.filter(customer =>
        customer.customer_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.country_iso.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Box minH="100vh" width="100%" pb={{ base: '90px', md: '20px' }} bg={backgroundColor}>
            <Container maxW="container.xl" py={{ base: 4, md: 8 }} px={{ base: 3, md: 6 }}>
                <VStack spacing={{ base: 4, md: 6 }} align="stretch">
                    {/* Header */}
                    <Box textAlign="center" pt={{ base: 2, md: 4 }}>
                        <Heading
                            size={{ base: 'lg', md: 'xl' }}
                            bgGradient={isDarkTheme 
                                ? `linear(to-r, ${accentColor}, ${primaryColor})` 
                                : `linear(to-r, ${primaryColor}, ${accentColor})`
                            }
                            bgClip="text"
                            mb={2}
                            fontWeight="extrabold"
                        >
                            <HStack justify="center" spacing={2}>
                                <Icon as={FaListUl} boxSize={{ base: 6, md: 8 }} />
                                <Text>Who To Call</Text>
                            </HStack>
                        </Heading>
                        <Text color={bodyTextColor} fontSize={{ base: 'sm', md: 'md' }} px={{ base: 4, md: 0 }}>
                            AI-prioritized list of customers to reach out to
                        </Text>
                    </Box>

                    {/* Statistics Cards */}
                    {!loading && (
                        <SimpleGrid columns={{ base: 3, md: 3 }} spacing={{ base: 3, md: 4 }}>
                            <Card 
                                bg={cardBg} 
                                borderRadius="xl" 
                                boxShadow="sm" 
                                border="1px" 
                                borderColor={lightBorderColor}
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaUsers} color={isDarkTheme ? accentColor : primaryColor} boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                Total Customers
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={isDarkTheme ? accentColor : primaryColor}>
                                            {callList.length}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card 
                                bg={cardBg} 
                                borderRadius="xl" 
                                boxShadow="sm" 
                                border="1px" 
                                borderColor={lightBorderColor}
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaChartLine} color={isDarkTheme ? accentColor : accentColor} boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                Avg Priority
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color={isDarkTheme ? accentColor : accentColor}>
                                            {avgPriority}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>

                            <Card 
                                bg={cardBg} 
                                borderRadius="xl" 
                                boxShadow="sm" 
                                border="1px" 
                                borderColor={lightBorderColor}
                            >
                                <CardBody p={{ base: 4, md: 5 }}>
                                    <Stat size={statSize}>
                                        <HStack spacing={2} mb={1}>
                                            <Icon as={FaShieldAlt} color="orange.500" boxSize={{ base: 4, md: 5 }} />
                                            <StatLabel fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>
                                                High Urgency
                                            </StatLabel>
                                        </HStack>
                                        <StatNumber fontSize={{ base: '2xl', md: '3xl' }} color="orange.600">
                                            {highUrgencyCount}
                                        </StatNumber>
                                    </Stat>
                                </CardBody>
                            </Card>
                        </SimpleGrid>
                    )}

                    {/* Error Alert */}
                    {error && (
                        <Alert 
                            status="error" 
                            borderRadius="xl" 
                            variant="left-accent" 
                            boxShadow="sm" 
                            bg={cardBg}
                            borderWidth="1px"
                            borderColor={borderColor}
                        >
                            <AlertIcon />
                            <Box flex="1">
                                <AlertTitle fontSize={{ base: 'sm', md: 'md' }} color={headingTextColor}>Error!</AlertTitle>
                                <AlertDescription fontSize={{ base: 'xs', md: 'sm' }} color={bodyTextColor}>{error}</AlertDescription>
                            </Box>
                        </Alert>
                    )}

                    {/* Search and Refresh */}
                    <Card 
                        bg={cardBg} 
                        borderRadius="lg" 
                        boxShadow="md" 
                        borderWidth="1px" 
                        borderColor={borderColor}
                    >
                        <CardBody>
                            <HStack spacing={4}>
                                <InputGroup flex={1}>
                                    <InputLeftElement pointerEvents="none">
                                        <Icon as={FaSearch} color={mutedIconColor} />
                                    </InputLeftElement>
                                    <Input
                                        placeholder="Search by Customer ID or Country..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        borderColor={borderColor}
                                        _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        bg={isDarkTheme ? 'rgba(45, 55, 72, 0.8)' : cardBg}
                                        color={bodyTextColor}
                                    />
                                </InputGroup>
                                <Button
                                    leftIcon={<FaSync />}
                                    bg={primaryColor}
                                    color="white"
                                    onClick={fetchWhoToCall}
                                    isLoading={loading}
                                    _hover={{ bg: buttonHoverBg }}
                                >
                                    Refresh
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>

                    {/* Customer List */}
                    {loading ? (
                        <Flex justify="center" align="center" py={20}>
                            <VStack spacing={4}>
                                <Spinner size="xl" color={primaryColor} thickness="4px" />
                                <Text color={mutedTextColor} fontSize="sm">Loading recommendations...</Text>
                            </VStack>
                        </Flex>
                    ) : filteredCallList.length === 0 ? (
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Box textAlign="center" py={10}>
                                    <Icon as={searchTerm ? FaSearch : FaPhone} boxSize={{ base: 10, md: 12 }} color={mutedIconColor} mb={4} />
                                    <Text color={mutedTextColor} fontSize={{ base: 'md', md: 'lg' }} fontWeight="medium">
                                        {searchTerm ? `No customers found matching "${searchTerm}"` : 'No customers to call at this time'}
                                    </Text>
                                    {searchTerm && (
                                        <Button
                                            mt={4}
                                            variant="link"
                                            color={isDarkTheme ? accentColor : primaryColor}
                                            onClick={() => setSearchTerm('')}
                                        >
                                            Clear search
                                        </Button>
                                    )}
                                </Box>
                            </CardBody>
                        </Card>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {filteredCallList.map((customer, index) => (
                                <Card
                                    key={customer.customer_id || index}
                                    bg={cardBg}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    transition="all 0.2s"
                                    _hover={{ boxShadow: 'lg' }}
                                    borderWidth="1px"
                                    borderColor={borderColor}
                                >
                                    <CardBody>
                                        <Flex justify="space-between" align="center">
                                            <VStack align="start" spacing={1} flex={1}>
                                                <Heading size="sm" color={headingTextColor}>
                                                    {customer.customer_id}
                                                </Heading>
                                                <HStack spacing={4} flexWrap="wrap">
                                                    <Badge
                                                        colorScheme={getUrgencyColor(customer.urgency_level)}
                                                        px={3}
                                                        py={1}
                                                        borderRadius="full"
                                                        fontSize="xs"
                                                        fontWeight="bold"
                                                    >
                                                        {customer.urgency_level}
                                                    </Badge>
                                                    <HStack spacing={1}>
                                                        <Icon as={FaMapMarkerAlt} color={mutedIconColor} boxSize={3.5} />
                                                        <Text fontSize="sm" color={bodyTextColor}>
                                                            {customer.country_iso}
                                                        </Text>
                                                    </HStack>
                                                    <HStack spacing={1}>
                                                        <Icon as={FaUser} color={mutedIconColor} boxSize={3.5} />
                                                        <Text fontSize="sm" color={bodyTextColor}>
                                                            {customer.age} years
                                                        </Text>
                                                    </HStack>
                                                    <HStack spacing={1}>
                                                        <Text fontSize="xs" color={mutedTextColor} fontWeight="medium">
                                                            Priority:
                                                        </Text>
                                                        <Text
                                                            fontSize="xs"
                                                            color={`${getPriorityColor(customer.priority_score)}.600`}
                                                            fontWeight="bold"
                                                        >
                                                            {customer.priority_score}/10
                                                        </Text>
                                                    </HStack>
                                                </HStack>
                                            </VStack>
                                            <IconButton
                                                icon={
                                                    expandedCustomer === customer.customer_id ? (
                                                        <ChevronUpIcon />
                                                    ) : (
                                                        <ChevronDownIcon />
                                                    )
                                                }
                                                onClick={() => toggleCustomer(customer.customer_id)}
                                                colorScheme="blue"
                                                variant="ghost"
                                                aria-label="Toggle details"
                                            />
                                        </Flex>

                                        <Collapse in={expandedCustomer === customer.customer_id} animateOpacity>
                                            <Box mt={6} pt={6} borderTop="1px solid" borderColor={borderColor}>
                                                <VStack align="stretch" spacing={4}>
                                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                                        <Card 
                                                            bg={infoBg} 
                                                            borderRadius="md" 
                                                            boxShadow="sm" 
                                                            borderWidth="1px" 
                                                            borderColor={borderColor}
                                                        >
                                                            <CardBody>
                                                                <HStack spacing={2} mb={3}>
                                                                    <Icon as={FaUser} color={isDarkTheme ? accentColor : primaryColor} boxSize={5} />
                                                                    <Text fontWeight="bold" color={labelTextColor}>Customer Details</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={2}>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Customer ID:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                            {customer.customer_id}
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Age:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                            {customer.age} years
                                                                        </Text>
                                                                    </HStack>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Country:</Text>
                                                                        <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                            {customer.country_iso}
                                                                        </Text>
                                                                    </HStack>
                                                                    <Divider borderColor={borderColor} />
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Risk Profile:</Text>
                                                                        <Badge
                                                                            colorScheme={getRiskColor(customer.risk_profile)}
                                                                            px={3}
                                                                            py={1}
                                                                            borderRadius="full"
                                                                            fontSize="xs"
                                                                            fontWeight="bold"
                                                                        >
                                                                            {getRiskLabel(customer.risk_profile)} ({customer.risk_profile}/10)
                                                                        </Badge>
                                                                    </HStack>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>

                                                        <Card 
                                                            bg={infoBg} 
                                                            borderRadius="md" 
                                                            boxShadow="sm" 
                                                            borderWidth="1px" 
                                                            borderColor={borderColor}
                                                        >
                                                            <CardBody>
                                                                <HStack spacing={2} mb={3}>
                                                                    <Icon as={FaChartLine} color={isDarkTheme ? accentColor : primaryColor} boxSize={5} />
                                                                    <Text fontWeight="bold" color={labelTextColor}>Priority Information</Text>
                                                                </HStack>
                                                                <VStack align="stretch" spacing={3}>
                                                                    <Box>
                                                                        <HStack justify="space-between" mb={1}>
                                                                            <Text fontSize="sm" color={bodyTextColor}>Priority Score:</Text>
                                                                            <Text
                                                                                fontSize="sm"
                                                                                color={`${getPriorityColor(customer.priority_score)}.600`}
                                                                                fontWeight="bold"
                                                                            >
                                                                                {customer.priority_score}/10
                                                                            </Text>
                                                                        </HStack>
                                                                        <Progress
                                                                            value={customer.priority_score * 10}
                                                                            size="sm"
                                                                            colorScheme={getPriorityColor(customer.priority_score)}
                                                                            borderRadius="full"
                                                                        />
                                                                    </Box>
                                                                    <HStack justify="space-between">
                                                                        <Text fontSize="sm" color={bodyTextColor}>Urgency Level:</Text>
                                                                        <Badge
                                                                            colorScheme={getUrgencyColor(customer.urgency_level)}
                                                                            px={3}
                                                                            py={1}
                                                                            borderRadius="full"
                                                                            fontSize="xs"
                                                                            fontWeight="bold"
                                                                        >
                                                                            {customer.urgency_level}
                                                                        </Badge>
                                                                    </HStack>
                                                                </VStack>
                                                            </CardBody>
                                                        </Card>
                                                    </SimpleGrid>

                                                    {/* Recommended Action */}
                                                    <Card 
                                                        bg={actionBg} 
                                                        borderRadius="md" 
                                                        boxShadow="sm" 
                                                        borderWidth="1px" 
                                                        borderColor={borderColor}
                                                    >
                                                        <CardBody>
                                                            <HStack spacing={2} mb={2}>
                                                                <Icon as={FaLightbulb} color={isDarkTheme ? accentColor : accentColor} boxSize={5} />
                                                                <Text fontWeight="bold" color={labelTextColor}>Recommended Action</Text>
                                                            </HStack>
                                                            <Text fontSize="sm" color={labelTextColor} lineHeight="1.6">
                                                                {customer.recommended_action}
                                                            </Text>
                                                        </CardBody>
                                                    </Card>
                                                </VStack>
                                            </Box>
                                        </Collapse>
                                    </CardBody>
                                </Card>
                            ))}
                        </VStack>
                    )}

                    {/* Summary footer */}
                    {!loading && filteredCallList.length > 0 && (
                        <Card 
                            bg={cardBg} 
                            borderRadius="lg" 
                            boxShadow="md" 
                            borderWidth="1px" 
                            borderColor={borderColor}
                        >
                            <CardBody>
                                <Text fontSize="sm" color={bodyTextColor} textAlign="center">
                                    Showing {filteredCallList.length} of {callList.length} customers
                                </Text>
                            </CardBody>
                        </Card>
                    )}
                </VStack>
            </Container>
        </Box>
    );
}

export default DashboardRMwhoToCallPaid;